import { cleanZyXML, removeAttributeFromXml } from './utils';

export type AttributeDef = {
    name: string;
    type: 'string' | 'boolean' | 'number' | 'array';
    xmlName?: string;
};

type ParseZyXMLConfig = {
    rootTag: string;
    xmlPropName: string;
    attributeToRemoveFromXml?: string[];
    attributes: AttributeDef[];
    hasOptions?: boolean;
    hasInstructions?: boolean;
    hasAltDescription?: boolean;
};

const parseElement = (element: Element): any => {
    const type = element.getAttribute('type');
    if (type === 'boolean') {
        return element.textContent?.trim() === 'true';
    } else if (type === 'number') {
        const text = element.textContent?.trim() ?? '';
        return text === '' ? '' : Number(text);
    } else if (type === 'list') {
        return Array.from(element.children).map(parseElement);
    } else if (type === 'dict' || element.children.length > 0) {
        return Object.fromEntries(Array.from(element.children).map(child => [child.tagName, parseElement(child)]));
    } else {
        return element.textContent?.trim() || '';
    }
};

export function parseZyXML(xml: string, config: ParseZyXMLConfig): Record<string, any> | null {
    if (!xml) return null;
    try {
        let cleanedXml = cleanZyXML(xml);
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(cleanedXml, 'text/xml');

        if (xmlDoc.querySelector('parsererror')) {
            console.error('Error parsing XML:', xmlDoc.querySelector('parsererror')?.textContent);
            return null;
        }

        const {
            rootTag,
            xmlPropName,
            attributes,
            attributeToRemoveFromXml = ['id'],
            hasOptions,
            hasAltDescription,
            hasInstructions,
        } = config;

        const rootElement = xmlDoc.querySelector(rootTag);

        // Remove specified attributes from root element before serializing
        if (rootElement && attributeToRemoveFromXml) {
            attributeToRemoveFromXml.forEach(attr => {
                cleanedXml = removeAttributeFromXml(cleanedXml, attr);
            });
        }

        const updates: Record<string, any> = {
            [xmlPropName]: cleanedXml,
        };

        if (rootElement) {
            attributes.forEach(attr => {
                const xmlAttrName = attr.xmlName || attr.name;
                const value = rootElement.getAttribute(xmlAttrName);

                switch (attr.type) {
                    case 'boolean':
                        updates[attr.name] = value === 'true';
                        break;
                    case 'number':
                        updates[attr.name] = value ? parseInt(value, 10) : undefined;
                        break;
                    case 'array':
                        if (value !== null) {
                            updates[attr.name] = value ? value.split(',').filter(id => id.trim() !== '') : [];
                        }
                        break;
                    case 'string':
                    default:
                        updates[attr.name] = value;
                }
            });
        }

        if (hasOptions) {
            const zyOptions = xmlDoc.querySelector('zyOptions');
            if (zyOptions) {
                const optionsObj: Record<string, any> = {};
                Array.from(zyOptions.children).forEach(child => {
                    optionsObj[child.tagName] = parseElement(child);
                });
                updates.options = optionsObj;
            }
        }

        if (hasAltDescription) {
            const zyAltDescription = xmlDoc.querySelector('zyAltDescription');
            if (zyAltDescription) {
                updates.alt_text = zyAltDescription.textContent || '';
            }
        }

        if (hasInstructions) {
            const zyInstructions = xmlDoc.querySelector('zyInstructions');
            if (zyInstructions) {
                updates.instructions = zyInstructions.innerHTML || '';
            }
        }

        return updates;
    } catch (error) {
        console.error('Error parsing zy XML:', error);
        return null;
    }
}
